import { state } from '../state';
import { get, post } from '../api';
import { showToast } from '../components/toast';
import { showContextMenu } from '../components/context-menu';
import type { ContextMenuItem } from '../components/context-menu';
import { openProfileCard } from '../components/profile-card';
import {
  fetchThreadStatusBatch,
  getDevSession,
  getDevSessionLog,
  statusCardHtml,
  type DevSession,
} from './dev-sessions';
import { mountLiveTailPanel, type LiveTailHandle } from './dev-session-tail';
import { realtime } from '../realtime';
import type { RealtimeEvent } from '../realtime';
import { parseIsoUtc } from '../util';
import { fmtMessageHeaderTime } from '../util/time';
import {
  mountMessageCompose,
  type ComposeHandle,
} from '../components/message-compose';
import {
  renderMarkdown,
  showComposeEmojiPicker,
  openGifPicker,
  openStickerPicker,
  showMentionAutocomplete,
  showSlashAutocomplete,
  showChannelAutocomplete,
  showEmojiAutocomplete,
  handleMentionKeydown,
  handleSlashKeydown,
  handleEmojiKeydown,
} from './chat';

interface Message {
  id: string;
  server_id: string;
  channel: string;
  author_id: string;
  content: string;
  edited_at: string | null;
  reply_to: string | null;
  attachments: string;
  reactions: string;
  pinned: number;
  created_at: string;
}

interface Thread {
  id: string;
  server_id: string;
  channel: string;
  parent_message_id: string;
  title: string;
  created_by: string;
  created_at: string;
  last_message_at: string | null;
  message_count: number;
  archived: number;
}

export interface ThreadSummary {
  thread_id: string;
  parent_message_id: string;
  message_count: number;
  last_message_at: string | null;
}

let panelPollTimer: ReturnType<typeof setInterval> | null = null;
let panelLastMsgId = '';
let panelThreadId = '';
let panelServerId = '';
let panelChannel = '';
let realtimeUnsub: (() => void) | null = null;
/** Shared compose component for the thread panel. */
let threadComposeHandle: ComposeHandle | null = null;

/** Track seen message IDs for WS+poll dedup */
const seenMsgIds = new Set<string>();

/** Scroll-to-latest + unseen-pill state for the thread panel. Panel is a
 *  singleton so module-level is fine — reset in closeThreadPanel. */
let threadUnseenCount = 0;
let threadIsAtBottom = true;
let threadPillEl: HTMLButtonElement | null = null;

function ensureThreadPill(): HTMLButtonElement | null {
  const panel = document.querySelector('.thread-panel') as HTMLElement | null;
  const messagesEl = document.getElementById('thread-panel-messages');
  if (!panel || !messagesEl) return null;
  if (threadPillEl && panel.contains(threadPillEl)) return threadPillEl;
  const p = document.createElement('button');
  p.type = 'button';
  p.className = 'chat-newmsg-pill';
  p.style.display = 'none';
  p.addEventListener('click', () => {
    messagesEl.scrollTo({ top: messagesEl.scrollHeight, behavior: 'smooth' });
  });
  panel.appendChild(p);
  threadPillEl = p;
  return p;
}

function updateThreadPill(): void {
  const p = ensureThreadPill();
  if (!p) return;
  if (threadUnseenCount > 0 && !threadIsAtBottom) {
    p.textContent = `${threadUnseenCount} new message${threadUnseenCount === 1 ? '' : 's'} \u2193`;
    p.style.display = 'flex';
  } else {
    p.style.display = 'none';
  }
}

function bumpThreadUnseen(delta: number): void {
  if (threadIsAtBottom) return;
  threadUnseenCount += delta;
  const btn = document.getElementById('thread-scroll-btn');
  if (btn) btn.style.display = 'flex';
  updateThreadPill();
}

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

function authorName(id: string): string {
  return state.people.find(p => p.id === id)?.name || `Member-${id.substring(0, 6)}`;
}

/** Consistent per-author avatar color — same algorithm as chat.ts so a user
 *  has the same tile color in the thread panel as in the main feed. */
function avatarBg(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return `var(--avatar-c${(h % 12) + 1})`;
}

function formatStarted(iso: string): string {
  const d = parseIsoUtc(iso);
  if (!d) return '';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/** Per-message header timestamp in the thread side panel. Delegates to
 *  util/time.fmtMessageHeaderTime so threads, main chat, and DMs share one
 *  Discord-1:1 render (HUB_TZ-correct day buckets + leading-zero numeric
 *  older fallback "DD/MM/YYYY 3:31 pm"). The prior implementation used
 *  bare WebView-locale toLocaleDateString/Time with no tz arg and no
 *  leading zeros, drifting on non-en-AU systems. */
function formatTime(iso: string): string {
  return fmtMessageHeaderTime(iso);
}

/** Batch-fetch thread summaries for a list of parent message ids. */
export async function fetchThreadSummaries(messageIds: string[]): Promise<Map<string, ThreadSummary>> {
  const map = new Map<string, ThreadSummary>();
  if (messageIds.length === 0) return map;
  try {
    const ids = messageIds.join(',');
    const rows = await get<ThreadSummary[]>(`/api/threads/for-messages?ids=${encodeURIComponent(ids)}`);
    for (const r of rows) map.set(r.parent_message_id, r);
  } catch { /* silent — badges just won't render */ }
  return map;
}

/** Render a "N replies" badge under a parent message. */
export function renderThreadBadge(msgEl: Element, summary: ThreadSummary): void {
  const body = msgEl.querySelector('.msg-body');
  if (!body) return;
  if (body.querySelector('.msg-thread-badge')) return;
  const count = summary.message_count;
  const badge = document.createElement('button');
  badge.className = 'msg-thread-badge';
  badge.dataset['thread'] = summary.thread_id;
  badge.innerHTML = `<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" style="opacity:0.6"><path d="M5 3v18h14v-8h-4a2 2 0 0 1-2-2V3H5zm10 0v6h6l-6-6z"/></svg>
    <span>${count} ${count === 1 ? 'reply' : 'replies'}</span>`;
  body.appendChild(badge);
}

/**
 * Open a thread panel directly from a thread id. Used by #devs-launch
 * (batch 2B-a) where the session card only carries the thread id, not the
 * full parent Message object. Fetches the thread row, then synthesises a
 * minimal Message stub for the header display. The panel's own polling +
 * dev-session overlay attaches as usual once panelThreadId is set.
 */
export async function openThreadPanelById(threadId: string): Promise<void> {
  try {
    const thread = await get<Thread>(`/api/threads/${encodeURIComponent(threadId)}`);
    const stubParent: Message = {
      id: thread.parent_message_id,
      server_id: thread.server_id,
      channel: thread.channel,
      author_id: thread.created_by,
      content: thread.title || '(session thread)',
      edited_at: null,
      reply_to: null,
      attachments: '[]',
      reactions: '[]',
      pinned: 0,
      created_at: thread.created_at,
    };
    await openThreadPanel(stubParent);
  } catch (e) {
    showToast(`Failed to open thread: ${e}`, 'err');
  }
}

/** Open (or create) a thread for the given parent message. */
export async function openThreadPanel(parent: Message): Promise<void> {
  closeThreadPanel();

  const container = document.querySelector('.chat-container') || document.querySelector('.dl-body') || document.querySelector('.content-area') || document.body;
  if (!container) return;

  const parentAuthor = authorName(parent.author_id);
  const parentInitial = parentAuthor.charAt(0).toUpperCase();
  const parentTime = formatTime(parent.created_at);
  const startedOn = formatStarted(parent.created_at);

  const panel = document.createElement('aside');
  panel.className = 'thread-panel';
  panel.setAttribute('role', 'complementary');
  panel.setAttribute('aria-label', 'Thread');
  panel.innerHTML = `
    <div class="thread-panel-header">
      <div class="thread-panel-header-left">
        <button class="thread-panel-back" id="thread-panel-back" aria-label="Back" type="button">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg>
        </button>
        <svg class="thread-panel-icon" viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M5 3v18h14v-8h-4a2 2 0 0 1-2-2V3H5zm10 0v6h6l-6-6z"/></svg>
      </div>
      <div class="thread-panel-header-center">
        <div class="thread-panel-title" id="thread-panel-title">Thread</div>
        <div class="thread-panel-subtitle">${esc(parentAuthor)} &middot; ${esc(parentTime)}</div>
      </div>
      <div class="thread-panel-header-right">
        <button class="thread-panel-menu" id="thread-panel-menu" aria-label="Thread options" type="button">&#8942;</button>
        <button class="thread-panel-close" id="thread-panel-close" aria-label="Close thread" type="button">&times;</button>
      </div>
    </div>
    <div class="thread-panel-parent">
      <div class="thread-parent-avatar" style="background:${avatarBg(parent.author_id)}">${esc(parentInitial)}</div>
      <div class="thread-parent-body">
        <div class="thread-parent-head">
          <span class="thread-parent-author">${esc(parentAuthor)}</span>
          <span class="thread-parent-time">${esc(parentTime)}</span>
        </div>
        <div class="thread-parent-content">${esc(parent.content)}</div>
      </div>
    </div>
    <div class="thread-started-marker">Started by ${esc(parentAuthor)}${startedOn ? ' &middot; ' + esc(startedOn) : ''}</div>
    <div class="thread-panel-messages" id="thread-panel-messages" role="log" aria-live="polite" aria-label="Thread messages">
      <div class="thread-loading">Loading…</div>
    </div>
    <button class="chat-scroll-btn" id="thread-scroll-btn" style="display:none" title="Jump to latest" aria-label="Jump to latest">
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/></svg>
    </button>
    <div class="thread-panel-compose" id="thread-compose-mount"></div>
  `;
  container.appendChild(panel);

  // Close handlers
  document.getElementById('thread-panel-close')?.addEventListener('click', closeThreadPanel);
  document.getElementById('thread-panel-back')?.addEventListener('click', closeThreadPanel);

  // 3-dot menu (stub v1)
  document.getElementById('thread-panel-menu')?.addEventListener('click', (e) => {
    const items: ContextMenuItem[] = [
      { label: 'Members', action: () => showToast('Coming soon', 'ok') },
      { label: 'Notifications', action: () => showToast('Coming soon', 'ok') },
      { label: 'Leave Thread', action: () => showToast('Coming soon', 'ok') },
    ];
    showContextMenu(e.clientX, e.clientY, items);
  });

  // Mobile: click outside to close + swipe-right-from-left-edge to close
  if (window.innerWidth <= 860) {
    const backdrop = document.createElement('div');
    backdrop.className = 'thread-panel-backdrop';
    container.insertBefore(backdrop, panel);
    backdrop.addEventListener('click', closeThreadPanel);

    // Swipe-right-from-left-edge gesture to close panel
    let swipeStartX = 0;
    let swipeStartY = 0;
    let swiping = false;
    panel.addEventListener('touchstart', (e) => {
      const touch = e.touches[0];
      if (touch && touch.clientX < 30) {
        swipeStartX = touch.clientX;
        swipeStartY = touch.clientY;
        swiping = true;
      }
    }, { passive: true });
    panel.addEventListener('touchmove', (e) => {
      if (!swiping || !e.touches[0]) return;
      const dy = Math.abs(e.touches[0].clientY - swipeStartY);
      if (dy > 30) { swiping = false; }
    }, { passive: true });
    panel.addEventListener('touchend', (e) => {
      if (!swiping || !e.changedTouches[0]) { swiping = false; return; }
      const dx = e.changedTouches[0].clientX - swipeStartX;
      swiping = false;
      if (dx > 80) closeThreadPanel();
    }, { passive: true });

    // Soft-keyboard lift for the thread composer is handled in CSS now
    // (.thread-panel-compose two-layer margin-bottom — see channels.css). The
    // old visualViewport math no-op'd on Chromium (overlaysContent=true keeps
    // vv.height full), which left the reply box hidden behind the keyboard.
    // The global initKeepFocusAboveKeyboard() in app.ts scrolls focus into view.
  }

  // Create-or-get the thread.
  let thread: Thread;
  try {
    const myId = state.ownerId || state.people[0]?.id || '';
    thread = await post<Thread>(`/api/messages/${encodeURIComponent(parent.id)}/thread`, {
      created_by: myId,
    });
  } catch (e) {
    showToast(`Failed to open thread: ${e}`, 'err');
    closeThreadPanel();
    return;
  }

  panelThreadId = thread.id;
  panelServerId = thread.server_id;
  panelChannel = thread.channel;
  seenMsgIds.clear();

  // Use the thread's actual title in the header once the row is known.
  const titleEl = document.getElementById('thread-panel-title');
  if (titleEl && thread.title && thread.title.trim()) {
    titleEl.textContent = thread.title;
  }

  await loadThreadMessages();
  // If this thread is tracked as a dev session, decorate the panel with the
  // status cards + metrics line + approve/reject buttons.
  void renderDevSessionOverlay(thread.id);

  // Left-click on avatar or author name opens the profile card — Discord parity.
  // Channel chat has this; threads never got it so clicking a reply author silently did nothing.
  const messagesContainer = document.getElementById('thread-panel-messages');

  // Scroll-to-latest button + "X new messages" pill — parity with channel chat
  // and DMs (pass 13). Without these, a user reading back through thread history
  // silently misses new replies arriving at the bottom.
  threadUnseenCount = 0;
  threadIsAtBottom = true;
  threadPillEl = null;
  const threadScrollBtn = document.getElementById('thread-scroll-btn') as HTMLButtonElement | null;
  messagesContainer?.addEventListener('scroll', () => {
    if (!messagesContainer) return;
    threadIsAtBottom = messagesContainer.scrollHeight - messagesContainer.scrollTop - messagesContainer.clientHeight < 100;
    if (threadScrollBtn) threadScrollBtn.style.display = threadIsAtBottom ? 'none' : 'flex';
    if (threadIsAtBottom) { threadUnseenCount = 0; updateThreadPill(); }
  });
  threadScrollBtn?.addEventListener('click', () => {
    if (!messagesContainer) return;
    messagesContainer.scrollTo({ top: messagesContainer.scrollHeight, behavior: 'smooth' });
    threadScrollBtn.style.display = 'none';
    threadUnseenCount = 0;
    updateThreadPill();
  });

  messagesContainer?.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    // Quick-bar button clicks take priority over the avatar/author profile path.
    const quickBtn = target.closest('.msg-quick-btn') as HTMLElement | null;
    if (quickBtn) {
      e.stopPropagation();
      const action = quickBtn.getAttribute('data-action') ?? '';
      const msgEl = quickBtn.closest('.thread-msg') as HTMLElement | null;
      if (!msgEl) return;
      const msgId = msgEl.getAttribute('data-id') ?? '';
      if (action === 'copy') {
        const text = msgEl.querySelector('.thread-msg-content')?.textContent ?? '';
        void navigator.clipboard.writeText(text);
        showToast('Copied');
      } else if (action === 'copyid') {
        void navigator.clipboard.writeText(msgId);
        showToast('Copied message ID');
      } else if (action === 'more') {
        const rect = quickBtn.getBoundingClientRect();
        msgEl.dispatchEvent(new MouseEvent('contextmenu', {
          bubbles: true, cancelable: true,
          clientX: rect.left, clientY: rect.bottom,
        }));
      }
      return;
    }
    const authorEl = target.classList.contains('thread-msg-author') ? target : null;
    const avatarEl = target.closest('.thread-msg-avatar') as HTMLElement | null;
    const anchor = authorEl || avatarEl;
    if (!anchor) return;
    const msgEl = anchor.closest('.thread-msg');
    const authorId = msgEl?.getAttribute('data-author');
    if (authorId) openProfileCard(authorId, anchor);
  });

  // Right-click context menu on thread messages (1b parity)
  messagesContainer?.addEventListener('contextmenu', (e) => {
    const msgEl = (e.target as HTMLElement).closest('.thread-msg');
    if (!msgEl) return;
    e.preventDefault();
    const text = msgEl.querySelector('.thread-msg-content')?.textContent ?? '';
    const authorEl = msgEl.querySelector<HTMLElement>('.thread-msg-author');
    const authorId = authorEl?.dataset['author'];
    const items: ContextMenuItem[] = [
      { label: 'Copy Text', action: () => { void navigator.clipboard.writeText(text); } },
      { label: 'Copy Message ID', action: () => {
        const id = (msgEl as HTMLElement).dataset['id'] ?? '';
        void navigator.clipboard.writeText(id);
      }},
    ];
    if (authorId) items.push({ label: 'View Profile', action: () => openProfileCard(authorId, msgEl as HTMLElement) });
    showContextMenu(e.clientX, e.clientY, items);
  });

  // Mount the shared compose component (same as channel chat).
  const composeMount = document.getElementById('thread-compose-mount');
  if (composeMount) {
    threadComposeHandle?.destroy();
    threadComposeHandle = mountMessageCompose(composeMount, {
      draftKey: `thread:${thread.id}`,
      thread_id: thread.id,
      placeholder: 'Reply in thread',
      maxLength: 4000,
      onSend: async (text) => { await sendThreadMessage(text); },
      onInput: (textarea) => {
        showMentionAutocomplete(textarea);
        showSlashAutocomplete(textarea);
        showChannelAutocomplete(textarea);
        showEmojiAutocomplete(textarea);
      },
      onKeydown: (e, textarea) => {
        if (handleMentionKeydown(e, textarea)) return true;
        if (handleEmojiKeydown(e, textarea)) return true;
        if (handleSlashKeydown(e, textarea)) return true;
        if (e.key === 'Escape') { closeThreadPanel(); return true; }
        return false;
      },
    });
    const threadInput = threadComposeHandle.textarea;
    threadComposeHandle.emojiBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      showComposeEmojiPicker(e.currentTarget as HTMLElement, threadInput);
    });
    threadComposeHandle.gifBtn?.addEventListener('click', (e) => {
      void openGifPicker(e.currentTarget as HTMLElement, threadInput);
    });
    threadComposeHandle.stickerBtn?.addEventListener('click', (e) => {
      void openStickerPicker(e.currentTarget as HTMLElement, threadInput);
    });
    threadComposeHandle.focus();
  }

  // Esc on document closes thread panel
  const escHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') closeThreadPanel();
  };
  document.addEventListener('keydown', escHandler);
  (panel as HTMLElement & { _escCleanup?: () => void })._escCleanup = () => {
    document.removeEventListener('keydown', escHandler);
  };

  // Wire realtime for thread messages
  wireThreadRealtime();

  // Adaptive polling: 3s if WS down, 60s just-in-case sync when WS healthy
  panelPollTimer = setInterval(() => {
    if (realtime.getState() !== 'open') {
      void pollThread();
    }
  }, 3000);
  const slowPollTimer = setInterval(() => {
    if (realtime.getState() === 'open') {
      void pollThread();
    }
  }, 60000);
  (panel as HTMLElement & { _slowPoll?: ReturnType<typeof setInterval> })._slowPoll = slowPollTimer;
}

function wireThreadRealtime(): void {
  if (realtimeUnsub) { realtimeUnsub(); realtimeUnsub = null; }
  if (!panelThreadId || !panelServerId || !panelChannel) return;

  realtime.connect();
  realtime.subscribeThread(panelThreadId);

  const off = realtime.on((ev: RealtimeEvent) => {
    if (ev.type === 'message_created' && ev.thread_id === panelThreadId) {
      const msg = ev.message;
      if (seenMsgIds.has(msg.id)) return;
      seenMsgIds.add(msg.id);
      const el = document.getElementById('thread-panel-messages');
      if (!el) return;
      if (el.querySelector(`[data-id="${msg.id}"]`)) return;
      if (el.querySelector('.thread-empty')) el.innerHTML = '';
      // Check 8-min grouping against last rendered message
      const lastMsg = el.querySelector('.thread-msg:last-child');
      const lastAuthor = lastMsg?.getAttribute('data-author') ?? '';
      const lastTime = lastMsg?.getAttribute('data-time') ?? '';
      const grouped = shouldGroup(lastAuthor, lastTime, msg.author_id, msg.created_at);
      el.insertAdjacentHTML('beforeend', renderThreadMessage(msg as Message, grouped));
      panelLastMsgId = msg.created_at;
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      if (nearBottom) {
        el.scrollTop = el.scrollHeight;
      } else {
        const myId = state.ownerId || state.people[0]?.id || '';
        if (msg.author_id !== myId) bumpThreadUnseen(1);
      }
    } else if (ev.type === 'message_edited' && ev.thread_id === panelThreadId) {
      const msg = ev.message;
      const oldEl = document.getElementById('thread-panel-messages')?.querySelector(`[data-id="${msg.id}"]`);
      if (oldEl) {
        const contentEl = oldEl.querySelector('.thread-msg-content');
        if (contentEl) contentEl.innerHTML = msg.content ? renderMarkdown(msg.content) : '';
      }
    } else if (ev.type === 'message_deleted' && ev.thread_id === panelThreadId) {
      document.getElementById('thread-panel-messages')?.querySelector(`[data-id="${ev.message_id}"]`)?.remove();
    }
  });
  realtimeUnsub = () => {
    off();
    realtime.unsubscribeThread(panelThreadId);
  };
}

async function loadThreadMessages(): Promise<void> {
  if (!panelThreadId) return;
  const el = document.getElementById('thread-panel-messages');
  if (!el) return;
  try {
    const msgs = await get<Message[]>(`/api/threads/${encodeURIComponent(panelThreadId)}/messages?limit=200`);
    if (msgs.length === 0) {
      el.innerHTML = '<div class="thread-empty">No replies yet — start the conversation.</div>';
      panelLastMsgId = '';
      return;
    }
    let html = '';
    for (let i = 0; i < msgs.length; i++) {
      const m = msgs[i]!;
      seenMsgIds.add(m.id);
      const prev = i > 0 ? msgs[i - 1]! : null;
      const grouped = prev ? shouldGroup(prev.author_id, prev.created_at, m.author_id, m.created_at) : false;
      html += renderThreadMessage(m, grouped);
    }
    el.innerHTML = html;
    el.scrollTop = el.scrollHeight;
    const last = msgs[msgs.length - 1];
    panelLastMsgId = last ? last.created_at : '';
  } catch {
    el.innerHTML = '<div class="thread-empty">Failed to load thread.</div>';
  }
}

async function pollThread(): Promise<void> {
  if (!panelThreadId || !panelLastMsgId) return;
  const el = document.getElementById('thread-panel-messages');
  if (!el) return;
  try {
    const msgs = await get<Message[]>(
      `/api/threads/${encodeURIComponent(panelThreadId)}/messages?after=${encodeURIComponent(panelLastMsgId)}&limit=50`,
    );
    if (msgs.length === 0) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (el.querySelector('.thread-empty')) el.innerHTML = '';
    const myId = state.ownerId || state.people[0]?.id || '';
    let unseenDelta = 0;
    for (const m of msgs) {
      if (seenMsgIds.has(m.id)) continue;
      seenMsgIds.add(m.id);
      if (el.querySelector(`[data-id="${m.id}"]`)) continue;
      const lastMsg = el.querySelector('.thread-msg:last-child');
      const lastAuthor = lastMsg?.getAttribute('data-author') ?? '';
      const lastTime = lastMsg?.getAttribute('data-time') ?? '';
      const grouped = shouldGroup(lastAuthor, lastTime, m.author_id, m.created_at);
      el.insertAdjacentHTML('beforeend', renderThreadMessage(m, grouped));
      panelLastMsgId = m.created_at;
      if (!nearBottom && m.author_id !== myId) unseenDelta++;
    }
    if (nearBottom) el.scrollTop = el.scrollHeight;
    else if (unseenDelta > 0) bumpThreadUnseen(unseenDelta);
  } catch { /* silent */ }
}

async function sendThreadMessage(text: string): Promise<void> {
  const content = text.trim();
  if (!content || !panelThreadId) return;
  const myId = state.ownerId || state.people[0]?.id || '';
  try {
    const msg = await post<Message>(`/api/threads/${encodeURIComponent(panelThreadId)}/send`, {
      author_id: myId,
      content,
    });
    // Mark as seen immediately so WS doesn't double-render
    seenMsgIds.add(msg.id);
    threadComposeHandle?.clearDraft();
    threadComposeHandle?.resetInput();
    const el = document.getElementById('thread-panel-messages');
    if (el) {
      if (el.querySelector('.thread-empty')) el.innerHTML = '';
      const lastMsg = el.querySelector('.thread-msg:last-child');
      const lastAuthor = lastMsg?.getAttribute('data-author') ?? '';
      const lastTime = lastMsg?.getAttribute('data-time') ?? '';
      const grouped = shouldGroup(lastAuthor, lastTime, msg.author_id, msg.created_at);
      el.insertAdjacentHTML('beforeend', renderThreadMessage(msg, grouped));
      el.scrollTop = el.scrollHeight;
      panelLastMsgId = msg.created_at;
    }
  } catch (e) {
    showToast(`Send failed: ${e}`, 'err');
    if (threadComposeHandle) { threadComposeHandle.textarea.value = content; }
  }
}

/** 8-min author grouping: same author within 8 minutes → collapse header */
function shouldGroup(prevAuthor: string, prevTime: string, curAuthor: string, curTime: string): boolean {
  if (!prevAuthor || prevAuthor !== curAuthor) return false;
  const prevTs = parseIsoUtc(prevTime)?.getTime() ?? 0;
  const curTs = parseIsoUtc(curTime)?.getTime() ?? 0;
  if (!prevTs || !curTs) return false;
  return (curTs - prevTs) < 8 * 60 * 1000;
}

function renderThreadQuickBar(msgId: string): string {
  const iconCopy = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>`;
  const iconHash = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>`;
  const iconMore = `<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>`;
  return `<div class="msg-quick-bar msg-quickbar msg-actions" data-msg-actions role="toolbar" aria-label="Message actions">
    <button class="msg-quick-btn" data-action="copy" data-msg="${esc(msgId)}" title="Copy Text" aria-label="Copy Text">${iconCopy}</button>
    <button class="msg-quick-btn" data-action="copyid" data-msg="${esc(msgId)}" title="Copy Message ID" aria-label="Copy Message ID">${iconHash}</button>
    <button class="msg-quick-btn" data-action="more" data-msg="${esc(msgId)}" title="More" aria-label="More">${iconMore}</button>
  </div>`;
}

function renderThreadMessage(m: Message, grouped: boolean): string {
  const name = authorName(m.author_id);
  const initial = name.charAt(0).toUpperCase();
  const time = formatTime(m.created_at);
  const body = m.content ? renderMarkdown(m.content) : '';
  const bg = avatarBg(m.author_id);
  const quickBar = renderThreadQuickBar(m.id);
  if (grouped) {
    return `<div class="thread-msg thread-msg-grouped" data-id="${esc(m.id)}" data-author="${esc(m.author_id)}" data-time="${esc(m.created_at)}">
      <div class="thread-msg-avatar" style="background:${bg}">${esc(initial)}</div>
      <div class="thread-msg-body">
        <div class="thread-msg-content">${body}</div>
      </div>
      ${quickBar}
    </div>`;
  }
  return `<div class="thread-msg" data-id="${esc(m.id)}" data-author="${esc(m.author_id)}" data-time="${esc(m.created_at)}">
    <div class="thread-msg-avatar" style="background:${bg}">${esc(initial)}</div>
    <div class="thread-msg-body">
      <div class="thread-msg-head">
        <span class="thread-msg-author" data-author="${esc(m.author_id)}">${esc(name)}</span>
        <span class="thread-msg-time">${esc(time)}</span>
      </div>
      <div class="thread-msg-content">${body}</div>
    </div>
    ${quickBar}
  </div>`;
}

export function closeThreadPanel(): void {
  if (panelPollTimer) { clearInterval(panelPollTimer); panelPollTimer = null; }
  if (liveTailHandle) { liveTailHandle.close(); liveTailHandle = null; }
  if (realtimeUnsub) { realtimeUnsub(); realtimeUnsub = null; }
  // Clean up slow poll timer
  const panel = document.querySelector('.thread-panel') as HTMLElement & { _slowPoll?: ReturnType<typeof setInterval>; _escCleanup?: () => void; _vvCleanup?: () => void } | null;
  if (panel?._slowPoll) { clearInterval(panel._slowPoll); }
  if (panel?._escCleanup) { panel._escCleanup(); }
  if (panel?._vvCleanup) { panel._vvCleanup(); }
  overlaySessionId = '';
  panelThreadId = '';
  panelServerId = '';
  panelChannel = '';
  panelLastMsgId = '';
  seenMsgIds.clear();
  threadComposeHandle?.destroy();
  threadComposeHandle = null;
  threadUnseenCount = 0;
  threadIsAtBottom = true;
  threadPillEl = null;
  document.querySelector('.thread-panel-backdrop')?.remove();
  document.querySelector('.thread-panel')?.remove();
  // Emit close event for other views
  window.dispatchEvent(new CustomEvent('hub-thread-closed'));
}

// 2B-b: the previous 5s overlay poll is gone. SSE (`/api/dev-sessions/:id/tail`)
// drives live state now, and refreshOverlay() runs only on initial mount and
// on observed status transitions.
let overlaySessionId = '';
let liveTailHandle: LiveTailHandle | null = null;

/**
 * If the current thread is tracked as a dev session, decorate the panel
 * with:
 *   1. A pinned LIVE panel (2B-b) above the message feed — status dot,
 *      elapsed timer, current tool badge, files-touched list, rolling
 *      assistant text tail, metrics, and approve/reject buttons. Fed by
 *      Server-Sent Events on `/api/dev-sessions/:id/tail`.
 *   2. The existing status-card TIMELINE below the live panel, sourced
 *      from `/api/dev-sessions/:id/log`. Refreshed on status transitions
 *      observed via SSE (not by a 5s poll any more).
 *
 * Architect redline #1: status colour comes from the backend's
 * `status_color` field (and from `statusToColor()` for raw kinds), never
 * from a stored column.
 */
async function renderDevSessionOverlay(threadId: string): Promise<void> {
  const map = await fetchThreadStatusBatch([threadId]);
  const view = map.get(threadId);
  if (!view || !view.session_id) return;
  overlaySessionId = view.session_id;

  // Inject overlay shell above the message list, inside the panel scroll area.
  const panel = document.querySelector('.thread-panel');
  const messages = document.getElementById('thread-panel-messages');
  if (!panel || !messages) return;
  if (panel.querySelector('.ds-thread-overlay')) return; // already mounted

  const overlay = document.createElement('div');
  overlay.className = 'ds-thread-overlay';
  overlay.id = 'ds-thread-overlay';
  overlay.innerHTML = `
    <div class="ds-live-panel-mount" id="ds-live-panel-mount"></div>
    <div class="ds-overlay-timeline" id="ds-overlay-timeline"></div>
  `;
  // Pin directly under the header (after parent preview)
  const parentEl = panel.querySelector('.thread-panel-parent');
  if (parentEl) {
    parentEl.insertAdjacentElement('afterend', overlay);
  } else {
    messages.parentElement?.insertBefore(overlay, messages);
  }

  await refreshOverlay();
}

async function refreshOverlay(): Promise<void> {
  if (!overlaySessionId) return;
  let session: DevSession;
  let logs: Awaited<ReturnType<typeof getDevSessionLog>>;
  try {
    [session, logs] = await Promise.all([
      getDevSession(overlaySessionId),
      getDevSessionLog(overlaySessionId),
    ]);
  } catch { return; /* keep last render */ }

  const mount = document.getElementById('ds-live-panel-mount');
  const timeline = document.getElementById('ds-overlay-timeline');
  if (!mount || !timeline) return;

  // Mount (or re-mount) the SSE-driven live panel. Closing an existing
  // handle before re-mounting prevents duplicate EventSources on status
  // transitions that force a re-render.
  if (liveTailHandle) { liveTailHandle.close(); liveTailHandle = null; }
  liveTailHandle = mountLiveTailPanel({
    session,
    panelEl: mount,
    onStatusChange: () => { void refreshOverlay(); },
  });

  // Append-only event timeline below the live panel. Uses the existing
  // status-card renderer so the visual language matches #devs-launch cards.
  timeline.innerHTML = logs.map(statusCardHtml).join('');
}
